import { OnTerminalLog, Serial } from './serial'
import { PcbPasswordResponse } from '@/api/definition'
import i18n from 'i18next'
import { generatePcbPassword, getLegacyPcbPassword } from '@/api/Device'
import { sleep } from '@/pages/utils/sleep'
import { isVersionGreaterThanOrEqual } from '@/pages/calibration/version'

const SHELL_DEBUG_ENABLE_TIMEOUT_MS = 10000
const SHELL_DEBUG_ENABLE_POLL_MS = 250

const getPassword = async (session: Serial): Promise<PcbPasswordResponse> => {
  const response = await session.sendCommand('get_version')
  const versionMatch = new RegExp(/software version:\s+(\d+\.\d+\.\d+)/).exec(
    response,
  )
  if (!versionMatch?.[1]) {
    throw new Error(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.errors.versionExtractFailed',
      ),
    )
  }
  if (isVersionGreaterThanOrEqual(versionMatch[1], '2.1.0')) {
    const uidMatch = new RegExp(/shell device UID:\s*([0-9a-fA-F]+)/).exec(
      response,
    )

    if (!uidMatch?.[1]) {
      throw new Error(
        i18n.t(
          'device.calibration.preFlightStep.certificateUploadFlow.errors.uidExtractFailed',
        ),
      )
    }
    return await generatePcbPassword(uidMatch[1])
  }
  return await getLegacyPcbPassword()
}

const enableShellDebug = async (
  session: Serial,
  password: string,
  onTerminalLog: OnTerminalLog,
) => {
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.enablingDebugShell',
    ),
  )
  let response = await session.sendCommand(`set_shell_debug ${password}`)

  const timeoutAt = Date.now() + SHELL_DEBUG_ENABLE_TIMEOUT_MS
  while (Date.now() < timeoutAt) {
    response += await session.readUntilOutput()
    if (response.includes('shell debug enabled')) {
      return
    }
    if (response.includes('shell invalid password')) {
      throw new Error(
        i18n.t(
          'device.calibration.preFlightStep.certificateUploadFlow.errors.invalidShellPassword',
        ),
      )
    }

    onTerminalLog(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.logs.waitingForDebugShell',
      ),
    )

    await sleep(SHELL_DEBUG_ENABLE_POLL_MS)
  }

  throw new Error(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.errors.enableDebugModeFailed',
    ),
  )
}

const sendHelpCommand = async (
  session: Serial,
  onTerminalLog: OnTerminalLog,
) => {
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.sendingHelpCommand',
    ),
  )
  await session.writeRaw('help\r\n')
  const response = await session.readUntilOutput()
  if (!response.includes('do_switch_mode')) {
    throw new Error(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.errors.deviceConnectionFailed',
      ),
    )
  }
}

export const switchToPassthroughMode = async (
  session: Serial,
  onTerminalLog: OnTerminalLog,
): Promise<void> => {
  // First command is not recognized. So we send a dummy command to get past it.
  await session.sendCommand('')
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.calculatingPassword',
    ),
  )
  const password = await getPassword(session)
  await enableShellDebug(session, password.password, onTerminalLog)
  await sendHelpCommand(session, onTerminalLog)

  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.switchingToPassthrough',
    ),
  )
  const response = await session.sendCommand('do_switch_mode passthrough')
  if (!response.includes('NMDI mode')) {
    throw new Error(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.errors.passthroughSwitchFailed',
      ),
    )
  }
}
